import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";
import { toast } from "sonner";
import { Copy, ExternalLink, RefreshCw, Wallet, Download, Radar, CheckCircle2, CircleDashed, CircleSlash, HelpCircle, Eye } from "lucide-react";
import { api, errMsg, BACKEND } from "../lib/api";
import { Panel, Loading, Empty, ErrorState } from "./State";

export const checkLogoFile = (f) => new Promise((resolve) => {
  if (f.type !== "image/png") return resolve("Logo must be a PNG image");
  if (f.size > 1024 * 1024) return resolve("Logo must be 1 MB or smaller");
  const img = new Image();
  const url = URL.createObjectURL(f);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const { naturalWidth: w, naturalHeight: h } = img;
    if (w !== h) return resolve(`Logo must be square — this image is ${w}×${h} px`);
    if (w < 256 || w > 2048) return resolve(`Logo must be 256–2048 px — this image is ${w}×${h} px`);
    resolve(null);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve("Couldn't read this image"); };
  img.src = url;
});

const copy = (v, label) => navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`));
export const TOKENLIST_URL = `${BACKEND}/api/metadata/tokenlist.json`;

export const AddToWallet = ({ meta, testid = "add-to-metamask" }) => {
  const { isConnected, chainId } = useAccount();
  const { data: wc } = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!isConnected || !wc) return toast.error("Connect your wallet first");
    setBusy(true);
    try {
      if (chainId !== meta.chainId) await switchChainAsync({ chainId: meta.chainId });
      const ok = await wc.watchAsset({ type: "ERC20", options: { address: meta.address, symbol: meta.symbol.slice(0, 11), decimals: meta.decimals, image: meta.logoURI || undefined } });
      ok ? toast.success(`${meta.symbol} added to your wallet`) : toast.message("Not added — you declined in the wallet");
    } catch (e) {
      if (e?.code === 4001 || /rejected|denied/i.test(e?.message || "")) toast.message("Not added — you declined in the wallet");
      else toast.error(/not supported|does not support|-32601/i.test(e?.message || "") ? "Your wallet doesn't support adding tokens this way — add the contract address manually" : errMsg(e));
    }
    setBusy(false);
  };
  return (
    <button data-testid={testid} onClick={add} disabled={busy} className="flex items-center gap-1 rounded-full border border-lime/40 px-3 py-1 text-[11px] text-lime hover:bg-lime/10 disabled:opacity-50">
      <Wallet className="h-3 w-3" /> {busy ? "Check your wallet…" : "Add to MetaMask"}
    </button>
  );
};

const STATUS = {
  live: ["Live · verified", "text-lime", CheckCircle2], indexed: ["Indexed · no logo yet", "text-warning", CircleDashed],
  not_found: ["Not listed", "text-slate-400", CircleDashed], unsupported: ["Network not supported", "text-slate-500", CircleSlash],
  manual: ["User action", "text-slate-300", Wallet], unknown: ["Unconfirmed", "text-slate-400", HelpCircle],
};

const ProviderRow = ({ r, provider }) => {
  const [label, tone, Icon] = STATUS[r.status] || STATUS.unknown;
  return (
    <li className="py-2.5 grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-3 text-xs" data-testid={`provider-${r.id}`}>
      <div className="sm:col-span-3 text-slate-100 font-semibold">{r.name}{provider?.cost && <span className="block text-[10px] font-normal text-slate-500">{provider.cost}</span>}</div>
      <div className={`sm:col-span-3 flex items-start gap-1.5 ${tone}`} data-testid={`provider-${r.id}-status`}><Icon className="h-3.5 w-3.5 shrink-0 mt-px" /><span>{label}<span className="block text-[10px] text-slate-500">{r.detail}</span></span></div>
      <div className="sm:col-span-4 text-[11px] text-slate-400">{provider?.how}{provider?.requirements && <span className="block text-[10px] text-slate-500">Needs: {provider.requirements}</span>}</div>
      <div className="sm:col-span-2 flex sm:flex-col gap-2 sm:items-end">
        {r.evidence_url && <a href={r.evidence_url} target="_blank" rel="noreferrer" data-testid={`provider-${r.id}-evidence`} className="text-slate-400 hover:text-lime flex items-center gap-1"><Eye className="h-3 w-3" /> Check</a>}
        {r.supported && r.submit_url && r.status !== "live" && <a href={r.submit_url} target="_blank" rel="noreferrer" data-testid={`provider-${r.id}-submit`} className="text-lime hover:underline flex items-center gap-1">Submit <ExternalLink className="h-3 w-3" /></a>}
      </div>
    </li>
  );
};

const ProviderStatus = ({ meta, providers }) => {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState(null);
  const load = (refresh) => {
    setErr(null);
    if (refresh) setSt(null);
    api.get(`/metadata/${meta.chainId}/${meta.address}/status`, { params: { refresh: refresh || undefined } }).then((r) => setSt(r.data)).catch((e) => setErr(errMsg(e)));
  };
  useEffect(() => { load(false); }, [meta.address]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorState testid="provider-status-error" message={err} onRetry={() => load(false)} />;
  if (!st) return <Loading label="Checking wallets & trackers" testid="provider-status-loading" />;
  return (
    <div data-testid="provider-status">
      <div className="flex items-center justify-between text-[10px] text-slate-500 pb-1">
        <span>“Live” is shown only when the provider's own public data confirms it. Checked {new Date(st.checked_at).toLocaleString()}.</span>
        <button data-testid="provider-status-refresh" onClick={() => load(true)} className="flex items-center gap-1 text-lime hover:underline"><RefreshCw className="h-3 w-3" /> Re-check</button>
      </div>
      <ul className="divide-y divide-white/5">{st.results.map((r) => <ProviderRow key={r.id} r={r} provider={providers.find((p) => p.id === r.id)} />)}</ul>
    </div>
  );
};

const Field = ({ k, v, testid, href }) => (
  <div className="flex items-center gap-2 py-1.5 border-b border-white/5 text-xs min-w-0">
    <span className="text-slate-500 w-28 shrink-0">{k}</span>
    {href ? <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="font-mono text-slate-200 hover:text-lime truncate">{v}</a>
      : <span data-testid={testid} className="font-mono text-slate-200 truncate">{v ?? "—"}</span>}
    {v && <button onClick={() => copy(String(v), k)} data-testid={`${testid}-copy`} className="ml-auto text-slate-500 hover:text-lime shrink-0"><Copy className="h-3 w-3" /></button>}
  </div>
);

const TokenCard = ({ c, providers }) => {
  const m = c.metadata;
  const id = m.address.toLowerCase();
  return (
    <div className="rounded-xl border border-white/10 bg-black/25 p-4 space-y-4" data-testid={`visibility-token-${id}`}>
      <div className="flex flex-wrap items-center gap-3">
        {m.logoURI ? <img src={m.logoURI} alt="" className="h-12 w-12 rounded-full object-cover ring-1 ring-lime/40" /> : <div className="hex h-12 w-12 bg-lime/10" />}
        <div className="min-w-0">
          <p className="font-display text-lg font-black text-white">{m.symbol} <span className="text-sm font-sans font-medium text-slate-400">{m.name}</span></p>
          <p className="text-[11px] text-slate-500">{m.network} · {m.hivevora.source === "hive_launch" ? "Launched on HIVEVORA" : m.hivevora.source === "original" ? "Original HIVEVORA token" : "Listed on HIVEVORA"}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <AddToWallet meta={m} testid={`visibility-add-wallet-${id}`} />
          <Link to={`/token/${m.address}?customize=1`} data-testid={`visibility-edit-${id}`} className="rounded-full border border-white/15 px-3 py-1 text-[11px] text-slate-300 hover:text-lime">{m.logoURI ? "Change logo" : "Upload logo"}</Link>
        </div>
      </div>
      {!m.logoURI && <p data-testid={`visibility-no-logo-${id}`} className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-warning">No logo yet — upload a square PNG (256–2048 px, ≤ 1 MB) to publish it to the token list and wallets.</p>}
      {c.logo && !c.logo.wallet_ready && <p className="text-xs text-warning">This logo was uploaded before the PNG rules — re-upload a square PNG for best wallet support.</p>}
      <div className="grid md:grid-cols-2 gap-x-6">
        <div>
          <Field k="Contract" v={m.address} testid={`visibility-address-${id}`} href={m.explorer} />
          <Field k="Chain ID" v={m.chainId} testid={`visibility-chain-${id}`} />
          <Field k="Decimals" v={m.decimals} testid={`visibility-decimals-${id}`} />
          <Field k="Website" v={m.website} testid={`visibility-website-${id}`} href={m.website} />
          {c.source_verified !== null && <Field k="Source code" v={c.source_verified ? "Verified on explorer" : "Not verified yet"} testid={`visibility-source-${id}`} />}
        </div>
        <div>
          <Field k="Logo URL" v={m.logoURI} testid={`visibility-logo-url-${id}`} href={m.logoURI} />
          <Field k="Metadata URL" v={m.hivevora.metadata} testid={`visibility-metadata-url-${id}`} href={m.hivevora.metadata} />
          <Field k="Token page" v={m.hivevora.page} testid={`visibility-page-${id}`} href={m.hivevora.page} />
          {m.logoURI && <div className="flex flex-wrap gap-2 pt-2">{Object.entries(c.resized).map(([s, u]) => (
            <a key={s} href={u} target="_blank" rel="noreferrer" data-testid={`visibility-logo-${s}-${id}`} className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-slate-300 hover:text-lime"><Download className="h-3 w-3" /> {s}×{s} PNG</a>
          ))}</div>}
        </div>
      </div>
      <ProviderStatus meta={m} providers={providers} />
    </div>
  );
};

export const TokenVisibility = () => {
  const [d, setD] = useState(null);
  const [providers, setProviders] = useState([]);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/metadata/mine").then((r) => setD(r.data.tokens)).catch((e) => setErr(errMsg(e)));
    api.get("/metadata/providers").then((r) => setProviders(r.data.providers)).catch(() => {});
  }, []);
  return (
    <Panel title="Token visibility" icon={Radar} testid="token-visibility-panel"
      right={<button onClick={() => copy(TOKENLIST_URL, "Token list URL")} data-testid="tokenlist-url-copy" className="text-xs text-lime flex items-center gap-1"><Copy className="h-3 w-3" /> HIVEVORA token list URL</button>}>
      <div className="p-5 space-y-4">
        <p className="text-xs text-slate-400 max-w-3xl">Publish your token metadata once, then use it to apply to wallets and trackers. Each provider reviews submissions itself — HIVEVORA never marks a listing as live until that provider's public data shows it. HIVEVORA will never ask for your seed phrase or private key.</p>
        {err ? <ErrorState testid="token-visibility-error" message={err} /> : !d ? <Loading /> : !d.length
          ? <Empty testid="token-visibility-empty" title="No published tokens yet" hint="Tokens you launch on HIVEVORA or get approved through a listing appear here." />
          : d.map((c) => <TokenCard key={`${c.metadata.chainId}-${c.metadata.address}`} c={c} providers={providers} />)}
      </div>
    </Panel>
  );
};

export const useTokenMetadata = (address, chainId = 4663) => {
  const [m, setM] = useState(null);
  useEffect(() => {
    setM(null);
    api.get(`/metadata/${chainId}/${address}`).then((r) => setM(r.data.metadata)).catch(() => setM(null));
  }, [address, chainId]);
  return m;
};
